/**
 * The account-scoped twin of `useReportMessage.ts`: report a chat member
 * directly, with no message to point at.
 *
 * Order: report first, then block. The report is the store-compliance action
 * and must not be lost to a block failure, so a failed report never attempts
 * the block at all.
 *
 * No confirm dialog lives here. Checking the box and pressing "Send report"
 * on `ReportAccountDialog` is the one confirming action for the "block them
 * too" convenience; raising a second confirm on top of it would contradict
 * that, which is why the block leg goes through `useBlockAndClearRequest`
 * rather than `useBlockFan`.
 */
import { useAccountReport } from "@sportsmet/messaging-sdk/private";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import type { typeReportReason } from "./moderationCopy";
import { isFailedSdkResult, sdkErrorCode } from "./moderationRules";
import { useBlockAndClearRequest } from "./useBlockFan";

export function useReportAccount(): {
  sendReport: (payload: {
    reportedAccountId: string;
    reason: typeReportReason;
    details: string | null;
    chatId: string | null;
    blockThemToo: boolean;
  }) => Promise<{
    didSend: boolean;
    didBlock: boolean;
    didClearRequest: boolean;
    errorCode: string | null;
  }>;
  isPending: boolean;
} {
  const reportMutation = useAccountReport();
  const { blockAndClear } = useBlockAndClearRequest();

  const sendReport = async (payload: {
    reportedAccountId: string;
    reason: typeReportReason;
    details: string | null;
    chatId: string | null;
    blockThemToo: boolean;
  }): Promise<{
    didSend: boolean;
    didBlock: boolean;
    didClearRequest: boolean;
    errorCode: string | null;
  }> => {
    try {
      const reportResult = await reportMutation.mutateAsync({
        reportedAccountId: payload.reportedAccountId,
        reason: payload.reason,
        details: payload.details,
        chatId: payload.chatId,
      });
      if (isFailedSdkResult(reportResult)) {
        showApiError(reportResult);
        return {
          didSend: false,
          didBlock: false,
          didClearRequest: false,
          errorCode: sdkErrorCode(reportResult),
        };
      }

      if (!payload.blockThemToo) {
        return {
          didSend: true,
          didBlock: false,
          didClearRequest: false,
          errorCode: null,
        };
      }

      const outcome = await blockAndClear({
        accountId: payload.reportedAccountId,
      });
      return {
        didSend: true,
        didBlock: outcome.didBlock,
        didClearRequest: outcome.didClearRequest,
        errorCode: outcome.errorCode,
      };
    } catch {
      showOfflineError();
      return {
        didSend: false,
        didBlock: false,
        didClearRequest: false,
        errorCode: null,
      };
    }
  };

  return { sendReport, isPending: reportMutation.isPending };
}
